import type { ParameterGroup } from "../generated/api/types.gen";

/** Gemini takes at most two reference images per edit; mirrors the backend's
 *  MAX_ATTRIBUTE_REFERENCE_IMAGES. */
export const MAX_COMPONENT_REPLACEMENTS = 2;

/** What the Replace modal hands back once the user has picked an image. */
export interface ComponentReplacementSelection {
  url: string;
  name: string;
  /** Set when the image came from (or was saved to) the asset library. */
  assetId?: string | null;
}

export function isGroupReplaced(
  group: Pick<ParameterGroup, "replacement_image_url">,
): boolean {
  return Boolean(group.replacement_image_url);
}

/** Apply a replacement to one group. The parameters are cleared: the image
 *  now describes the element, and stale attributes would otherwise ride along
 *  into the next refinement. */
export function applyReplacement(
  group: ParameterGroup,
  selection: ComponentReplacementSelection,
): ParameterGroup {
  return {
    ...group,
    parameters: [],
    replacement_image_url: selection.url,
    replacement_name: selection.name,
    replacement_asset_id: selection.assetId ?? null,
  };
}

/** Replacements attached since the current image — one applied on an earlier
 *  iteration is already baked into the image and costs no reference slot. */
export function countPendingReplacements(
  formGroups: ParameterGroup[] | null | undefined,
  originalGroups: ParameterGroup[] | null | undefined,
): number {
  const originalUrls = new Map(
    (originalGroups ?? []).map((g) => [g.group_name, g.replacement_image_url]),
  );
  return (formGroups ?? []).filter(
    (group) =>
      isGroupReplaced(group) &&
      group.replacement_image_url !== originalUrls.get(group.group_name),
  ).length;
}

/** Re-replacing a group that already has a pending replacement is always
 *  allowed — swapping its image doesn't claim an extra slot. */
export function canReplaceComponent(args: {
  group: ParameterGroup;
  formGroups: ParameterGroup[] | null | undefined;
  originalGroups: ParameterGroup[] | null | undefined;
}): boolean {
  const pending = countPendingReplacements(
    args.formGroups,
    args.originalGroups,
  );
  if (pending < MAX_COMPONENT_REPLACEMENTS) return true;
  return countPendingReplacements([args.group], args.originalGroups) > 0;
}
